import {FC, useState} from "react";
import {App, Button, Checkbox, Flex, Form, FormProps, Input, Select} from "antd";
import {UserContact, UserContactType} from "../../../../types/domain.ts";
import {CONTACT_TYPE_OPTIONS, CONTACT_TYPE_PLACEHOLDERS} from "../../../../constants/constants.ts";
import {MinusCircleOutlined, PlusOutlined, SaveOutlined} from "@ant-design/icons";
import styles from './ContactsForm.module.css';
import {usersUserIdContactsPost} from "../../../../api/users/contacts/usersUserIdContactsPost.ts";
import {useOwnProfile} from "../../../../stores/OwnProfileStore.ts";
import {usersUserIdContactsContactIdPatch} from "../../../../api/users/contacts/usersUserIdContactsContactIdPatch.ts";
import {usersUserIdContactsContactIdDelete} from "../../../../api/users/contacts/usersUserIdContactsContactIdDelete.ts";
import {
    usersUserIdContactsContactIdPrimaryPut
} from "../../../../api/users/contacts/usersUserIdContactsContactIdPrimaryPut.ts";
import {InputPhone} from "../../../../components/InputPhone/InputPhone.tsx";
import {contactRules, contactTransformers} from "./formUtils.ts";

type FormType = {
    contacts: {
        contactId?: string;
        type: UserContactType;
        value: string;
        primary: boolean;
    }[];
}

type Props = {
    contacts: UserContact[];
}

export const ContactsForm: FC<Props> = ({ contacts }) => {
    const { message } = App.useApp();
    const { profile } = useOwnProfile();
    const [form] = Form.useForm<FormType>();

    const [loading, setLoading] = useState(false);
    const handleFinish: FormProps<FormType>['onFinish'] = async (values) => {
        if(!profile) return; // exhaustive

        setLoading(true);
        try {
            const newContacts = values.contacts.filter(({ contactId, type, value }) => !contactId && type && value);
            const editedContacts = values.contacts.filter(({ contactId, type, value }) => {
                const existingContact = contacts.find(({ id }) => id === contactId);
                return existingContact && (type !== existingContact.type || value !== existingContact.value)
            });
            const deletedContacts = contacts.filter(({ id }) => !values.contacts.some(({ contactId }) => contactId === id));
            const primaryContact = values.contacts.find(({ primary }) => primary);
            let newPrimaryContact = primaryContact?.contactId && primaryContact.contactId !== contacts.find(({ primary }) => primary)?.id ?
                primaryContact :
                null; // will be null if primary contact is newly created - will be overwritten during creation

            if(newContacts.length) {
                await Promise.all(newContacts.map(({ type, value, primary }) => usersUserIdContactsPost(profile.id, {
                    type, value: contactTransformers[type](value)
                }).then(({ payload }) => {
                    if(primary) {
                        newPrimaryContact = {
                            contactId: payload.id,
                            ...payload
                        }
                        console.log('overwritten primary contact', newPrimaryContact);
                    }
                })));
                console.log('new contacts created', newContacts.length);
            }
            if(editedContacts.length) {
                await Promise.all(editedContacts.map(({ contactId, type, value }) => {
                    const existingContact = contacts.find(({ id }) => id === contactId)!;
                    return usersUserIdContactsContactIdPatch(profile.id, contactId!, {
                        type: type !== existingContact.type ? type : undefined,
                        value: contactTransformers[type](value) !== existingContact.value ? contactTransformers[type](value) : undefined
                    })
                }));
                console.log('contacts edited', editedContacts.length);
            }
            if(deletedContacts.length) {
                await Promise.all(deletedContacts.map(({ id }) => usersUserIdContactsContactIdDelete(profile.id, id)));
                console.log('contacts deleted', deletedContacts.length);
            }
            if(newPrimaryContact?.contactId) {
                await usersUserIdContactsContactIdPrimaryPut(profile.id, newPrimaryContact.contactId);
                console.log('changed primary contact', newPrimaryContact);
            }
            void message.success('Контактные данные обновлены');
        } catch (e) {
            console.error(e);
            void message.error('Ошибка при обновлении контактных данных (см консоль)');
        } finally {
            setLoading(false);
        }
    }

    const values = Form.useWatch('contacts', form);
    const getType = (name: number) => values?.[name]?.type || 'telegram';

    return (
        <Form<FormType>
            form={form}
            autoComplete="off"
            onFinish={handleFinish}
            preserve={false}
            initialValues={{
                contacts: contacts.map(({ id, type, value, primary }) => ({
                    contactId: id,
                    type,
                    value,
                    primary
                }))
            }}
        >
            <Flex vertical gap={12}>
                <Form.List name="contacts">
                    {(fields, { add, remove }) => (
                        <Flex vertical gap={8}>
                            {fields.map(({ key, name, ...restField }) => (
                                <Flex align="center" gap={8} key={key}>
                                    <Form.Item
                                        {...restField}
                                        noStyle
                                        name={[name, 'type']}
                                        rules={[{ required: true }]}
                                    >
                                        <Select
                                            className={styles.typeSelector}
                                            options={CONTACT_TYPE_OPTIONS}
                                            placeholder="Тип"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        noStyle
                                        name={[name, 'value']}
                                        rules={[contactRules[getType(name)]]}
                                    >
                                        {getType(name) === 'wa' || getType(name) === 'tel' ? (
                                            <InputPhone placeholder={CONTACT_TYPE_PLACEHOLDERS[getType(name)]} />
                                        ) : (
                                            <Input placeholder={CONTACT_TYPE_PLACEHOLDERS[getType(name)]} />
                                        )}
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        noStyle
                                        name={[name, 'primary']}
                                        valuePropName="checked"
                                    >
                                        <Checkbox>Основной</Checkbox>
                                    </Form.Item>
                                    <MinusCircleOutlined onClick={() => remove(name)} />
                                </Flex>
                            ))}
                            <Form.Item noStyle>
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Добавить
                                </Button>
                            </Form.Item>
                        </Flex>
                    )}
                </Form.List>
                <div>
                    <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={loading}>Сохранить</Button>
                </div>
            </Flex>
        </Form>
    )
};